import { ScannedCode, ScannedCodeType, ScannedCodeWithEntity } from 'app/features/scan-flow/models';
import { Activity } from 'app/shared/features/activity-selector/models';
import { Batch } from 'app/shared/features/batch-selector/models';
import { LocationType } from 'app/shared/features/location-selector/models';

const regexDict: Record<ScannedCodeType, RegExp> = {
    activity: /^\s*a(?<code>\d+)\s*$/i,
    batch: /^\s*b(?<code>\d+)\s*$/i,
    employee: /^\s*e(?<code>\d+)\s*$/i,
    location: /^\s*l(?<code>\d+)\s*$/i,
    container: /^\s*c(?<code>\d+)\s*$/i,
    'site-identifier': /^([^;]*;){3}[^;]*$/i,
};

export const getScannedCodeRegex = (codeType: ScannedCodeType) => regexDict[codeType];

export const parseScannedCode = ({
    code,
    allowedCodeTypes,
}: {
    code: string;
    allowedCodeTypes: ScannedCodeType[];
}): ScannedCode => {
    for (const codeType of allowedCodeTypes) {
        const match = code.match(regexDict[codeType]);
        if (match) {
            return {
                number: match.groups?.code ? Number(match.groups.code) : undefined,
                codeType,
                code,
                error: undefined,
            };
        }
    }

    const scannedCodeType = getCodeTypeFromScannedCode(code) as ScannedCodeType;

    return {
        number: Number(getCodeTypeFromScannedCode(code)),
        codeType: scannedCodeType,
        code,
        error: {
            message: scannedCodeType
                ? `SCAN.ERROR.TITLE.EXPECTED_CODE_BUT_WRONG_CODE_SCANNED`
                : 'SCAN.ERROR.TITLE.EXPECTED_CODE_BUT_UNKNOWN_CODE_SCANNED',
            expectedCodeType: allowedCodeTypes,
            scannedCodeType: scannedCodeType ? scannedCodeTitle[scannedCodeType] : undefined,
            shortScannedValue: getShortScannedCode(code),
        },
    };
};

export const getScannedCodeTitle = (allowedCodeType: ScannedCodeType) => {
    return scannedCodeTitle[allowedCodeType];
};

export const getShortScannedCode = (code: string): string => {
    return `${code.slice(0, 8)}${code.length > 8 ? '...' : ''}`;
};
export const scanCodeActionDescriptions: Record<ScannedCodeType, string> = {
    activity: 'SCAN.ACTION.TITLE.SCAN_ACTIVITY_CODE',
    batch: 'SCAN.ACTION.TITLE.SCAN_BATCH_CODE',
    container: 'SCAN.ACTION.TITLE.SCAN_CONTAINER_CODE',
    employee: 'SCAN.ACTION.TITLE.SCAN_EMPLOYEE_CODE',
    location: 'SCAN.ACTION.TITLE.SCAN_LOCATION_CODE',
    'site-identifier': 'SCAN.ACTION.TITLE.SCAN_SITE_CODE',
};

export const scannedCodeTitle: Record<ScannedCodeType, string> = {
    activity: 'GENERAL.TITLE.ACTIVITY',
    batch: 'GENERAL.TITLE.BATCH',
    employee: 'GENERAL.TITLE.EMPLOYEE',
    location: 'GENERAL.TITLE.LOCATION',
    container: 'GENERAL.TITLE.CONTAINER',
    'site-identifier': 'GENERAL.TITLE.SITE_IDENTIFIER',
};

export const scannedCodeLocationTypeTitle: Record<LocationType, string> = {
    0: 'GENERAL.LOCATION_TYPE.OTHER',
    10: 'GENERAL.LOCATION_TYPE.SITE',
    20: 'GENERAL.LOCATION_TYPE.GREENHOUSE',
    25: 'GENERAL.LOCATION_TYPE.PACKHOUSE',
    30: 'GENERAL.LOCATION_TYPE.ROW',
    35: 'GENERAL.LOCATION_TYPE.PACKAGE',
    40: 'GENERAL.LOCATION_TYPE.CROP',
    45: 'GENERAL.LOCATION_TYPE.DOOR',
};

export function createScannedCodeFromActivity(activity: Activity): ScannedCodeWithEntity<Activity> {
    return {
        code: 'A' + activity.number,
        codeType: 'activity',
        entity: activity,
        number: activity.number,
        error: undefined,
    };
}

export function createScannedCodeFromBatch(batch: Batch): ScannedCode {
    return {
        code: 'B' + batch.number,
        codeType: 'batch',
        number: batch.number,
        error: undefined,
    };
}

export const getCodeTypeFromScannedCode = (scannedCode: string) => {
    return (
        Object.keys(regexDict).find((key) => scannedCode.match(regexDict[key as ScannedCodeType])) ??
        undefined
    );
};

export const getScannedCodeNumbersByCodeType = (scannedCodes: ScannedCode[], codeType: ScannedCodeType) =>
    scannedCodes
        .filter((scannedCode) => scannedCode && scannedCode.codeType === codeType)
        .map((code) => code.number)
        .filter((number): number is number => !!number);

export const getActivityNumberFromScannedCodes = (scannedCodes: ScannedCode[]) => {
    const activityNumbers = getScannedCodeNumbersByCodeType(scannedCodes, 'activity');
    return activityNumbers.length ? activityNumbers[0] : undefined;
};

export const getBatchNumberFromScannedCodes = (scannedCodes: ScannedCode[]) => {
    const batchNumber = getScannedCodeNumbersByCodeType(scannedCodes, 'batch');
    return batchNumber.length ? batchNumber[0] : undefined;
};

export const getLocationNumbersFromScannedCodes = (scannedCodes: ScannedCode[]) => {
    return getScannedCodeNumbersByCodeType(scannedCodes, 'location');
};
